/**
 * The space's Board, and every page uploaded from Markdown: one live page
 * people and the AI edit at the same time over the space's real-time
 * document (`useSpaceDocument(spaceId, docId)`). A page is the Board's own
 * model, so it gets the same editor, carets and presence.
 *
 * The server decides who may edit: a person who may only view gets a
 * read-only page whatever this component is told, because the server drops
 * their writes. `canEdit` only shapes the hints.
 *
 * `LivePageView` is the part below the hook, so a browser fixture can drive
 * it with its own provider.
 *
 * @module components/spaces/board/SpaceBoardLive
 */
import { CloudOffIcon, EyeIcon, LoaderCircleIcon } from "lucide-react";
import { useEffect, useState } from "react";
import type { Awareness } from "y-protocols/awareness";
import type * as Y from "yjs";

import type { SpaceDocumentStatus } from "../../../spaces/realtime/SpaceDocumentProvider";
import { useSpaceDocument } from "../../../spaces/realtime/useSpaceDocument";
import { PresenceStack, useDocumentPresence } from "../DocumentPresence";
import { BoardEditor } from "./BoardEditor";

const PROVISIONAL_USER = { name: "You", color: "#64748b" };

/**
 * Says something only when the page is not simply up to date: connecting,
 * offline (edits kept on this device), view only, or access lost. A synced,
 * editable Board shows nothing.
 */
export function StatusNote({
  status,
  access,
  noun,
}: {
  readonly status: SpaceDocumentStatus;
  readonly access: "edit" | "view" | null;
  readonly noun: string;
}) {
  if (status === "offline") {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-400">
        <CloudOffIcon className="size-3.5" />
        Offline. Edits are kept on this device and sync when you reconnect.
      </span>
    );
  }
  if (status === "connecting") {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
        <LoaderCircleIcon className="size-3.5 animate-spin" />
        Connecting
      </span>
    );
  }
  if (status === "closed") {
    return <span className="text-xs text-muted-foreground">You no longer have access to this {noun}.</span>;
  }
  if (access === "view") {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
        <EyeIcon className="size-3.5" />
        View only
      </span>
    );
  }
  return null;
}

/**
 * Waits for the first sync before it paints, so a page never shows empty and
 * then fills. Kept per document: a component reused for another space or
 * document waits for that one's first sync, not the previous one's.
 */
export function useEverSynced(status: SpaceDocumentStatus, doc: Y.Doc | null): boolean {
  const [syncedDoc, setSyncedDoc] = useState<Y.Doc | null>(null);
  useEffect(() => {
    if (status === "synced" && doc !== null) setSyncedDoc(doc);
  }, [status, doc]);
  return status === "synced" || (doc !== null && syncedDoc === doc);
}

export function LivePageView({
  spaceId,
  docId,
  doc,
  awareness,
  status,
  access,
  canEdit,
}: {
  readonly spaceId: string;
  readonly docId: string;
  readonly doc: Y.Doc | null;
  readonly awareness: Awareness | null;
  readonly status: SpaceDocumentStatus;
  readonly access: "edit" | "view" | null;
  readonly canEdit: boolean;
}) {
  const presence = useDocumentPresence(awareness);
  const everSynced = useEverSynced(status, doc);
  const board = docId === "board";
  const ready = doc !== null && awareness !== null && everSynced;
  const editable = access === "edit" && status !== "closed";

  return (
    <section aria-label={board ? "Board" : "Page"} data-space-board={spaceId} data-space-doc={docId} className="w-full">
      {status !== "synced" || access === "view" || presence.length > 0 ? (
        <div className="mb-3 flex min-h-6 items-center justify-between gap-3">
          <StatusNote status={status} access={access} noun={board ? "Board" : "page"} />
          <PresenceStack entries={presence} />
        </div>
      ) : null}
      {ready ? (
        <BoardEditor
          doc={doc}
          awareness={awareness}
          editable={editable}
          user={PROVISIONAL_USER}
          placeholder={board ? undefined : "Write here. Type / for a heading, checklist, table or chart."}
        />
      ) : (
        <div className="space-y-3" aria-hidden="true">
          <div className="h-7 w-2/3 animate-pulse rounded-md bg-muted" />
          <div className="h-4 w-full animate-pulse rounded bg-muted" />
          <div className="h-4 w-5/6 animate-pulse rounded bg-muted" />
          <div className="h-4 w-3/4 animate-pulse rounded bg-muted" />
        </div>
      )}
      {ready && editable && canEdit ? (
        <p className="mt-2 text-xs text-muted-foreground">
          Type <kbd className="rounded border border-border px-1">/</kbd> to insert a heading,
          checklist, table or chart.{board ? " Ask a chat in this space to edit the Board for you." : ""}
        </p>
      ) : null}
    </section>
  );
}

export function SpaceBoardLive({
  spaceId,
  docId = "board",
  canEdit,
}: {
  readonly spaceId: string;
  /** `board`, or a page (`page:<uuid>`) uploaded from Markdown. */
  readonly docId?: string;
  readonly canEdit: boolean;
}) {
  const { doc, awareness, status, access } = useSpaceDocument(spaceId, docId);
  return (
    <LivePageView spaceId={spaceId} docId={docId} doc={doc} awareness={awareness} status={status} access={access} canEdit={canEdit} />
  );
}
